/**
 * AddressCardDe — "Conny Fälle in Ihrer Nähe" (German variant of AddressCard).
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="address-card-de"></div>
 *
 * Reads the same sessionStorage keys as AddressCard:
 *   STORAGE_KEYS.street, .house, .zip, .address
 */
import { useQuery } from "@tanstack/react-query";
import { getFunnelContext } from "../../shared/api";
import { readAddress } from "../../shared/address";
import { AnimatedNumber } from "../../shared/AnimatedNumber";
import { CheckIcon, PinIcon } from "../../shared/icons";
import { useStorage, STORAGE_KEYS } from "../../shared/storage";

function useReactiveAddress() {
  useStorage(STORAGE_KEYS.street);
  useStorage(STORAGE_KEYS.house);
  useStorage(STORAGE_KEYS.zip);
  useStorage(STORAGE_KEYS.address);
  return readAddress();
}

export function fallNoun(count: number): string {
  return count === 1 ? "Fall" : "Fälle";
}

export function AddressCardDe() {
  const addr = useReactiveAddress();
  const enabled = Boolean(addr.zip || (addr.street && addr.houseNumber));

  const { data } = useQuery({
    queryKey: ["funnel-context", addr.street, addr.houseNumber, addr.zip],
    queryFn: () =>
      getFunnelContext({
        street: addr.street,
        house: addr.houseNumber,
        zip: addr.zip,
      }),
    enabled,
    staleTime: 5 * 60_000,
  });

  if (!enabled) return null;

  const houseCount = data?.address?.house_cases ?? 0;
  const streetCount = data?.address?.street_cases ?? 0;
  const areaCount = data?.zip?.cases ?? 0;

  if (data && houseCount === 0 && streetCount === 0 && areaCount === 0)
    return null;

  return (
    <div className="cn">
      <div className="cn-card cn-fade-in">
        <div className="cn-top">
          <div className="cn-icon">
            <PinIcon />
          </div>
          <div className="cn-head">
            <span>Conny Fälle in Ihrer Nähe</span>
          </div>
        </div>

        <div className="cn-body">
          {houseCount > 0 && <Row label="In Ihrem Haus" count={houseCount} />}
          {streetCount > 0 && (
            <Row label="In Ihrer Straße" count={streetCount} />
          )}
          {areaCount > 0 && (
            <Row
              label={`In Ihrer Nachbarschaft (PLZ: ${addr.zip || "—"})`}
              count={areaCount}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, count }: { label: string; count: number }) {
  if (count <= 0) return null;
  const noun = fallNoun(count);
  return (
    <div className="cn-row">
      <span className="cn-row-left">
        <span className="cn-check">
          <CheckIcon />
        </span>
        <span className="cn-row-label">{label}</span>
      </span>
      <span className="cn-chip">
        <AnimatedNumber value={count} />
        &nbsp;{noun}
      </span>
    </div>
  );
}
